import { Controller } from "@hotwired/stimulus"
import SlimSelect, { Option } from "slim-select"
import { generateUrlWithQuery } from "~/helpers/url_helpers"
import Collection from "~/types/models/Collection.model"
import { debounceAsync } from "~/helpers/debounce_helpers"
import { get, post } from "@rails/request.js"

// Connects to data-controller="slim-select"
export default class SlimSelectController extends Controller<HTMLElement> {
  static values = {
    url: String,
    placeholder: String,
  }

  declare urlValue: string
  declare placeholderValue: string

  private slimSelect?: SlimSelect
  private lastSearchResults?: Partial<Option>[]
  private searchResultSelected: boolean = false

  get closeOnSelect() {
    return !this.element.getAttribute("multiple")
  }

  get isOpen() {
    return this.slimSelect?.settings?.isOpen
  }

  initialize() {
    this.search = debounceAsync(this.search.bind(this), 300)
  }

  connect() {
    this.slimSelect = new SlimSelect({
      select: this.element,
      settings: {
        closeOnSelect: this.closeOnSelect,
        allowDeselect: true,
        keepOrder: true,
        contentPosition: "fixed",
        contentLocation: this.element.parentElement,
        placeholderText: this.placeholderValue,
        maxSelected: 4,
      },
      events: {
        beforeChange: () => {
          this.searchResultSelected = true
          return true
        },
        afterChange: (newVal) => {
          if (!this.closeOnSelect && newVal.length && this.isOpen) {
            // On select, re-render the dropdown menu to the correct coordinates. This is not done automatically.
            // Only do it if the dropdown should not close on select, otherwise, the dropdown will remain open.
            this.rerender()
          }
          this.dispatch("after-change")
        },
        searchFilter: undefined,
        addable: (value: string) => {
          return post(this.urlValue, { headers: { Accept: "application/json" }, body: { name: value } })
            .then(async (response) => {
              const data: Collection = await response.json
              return { text: value, value: data.id.toString() }
            })
        },
        search: (query, currentlySelectedOptions) => {
          if (this.searchResultSelected) {
            // For some reason, slim select calls search again after an option is selected. This recalls the search
            // function and re-renders the options. This is a workaround to prevent that.
            return new Promise((resolve) => {
              const allOptions = this.slimSelect?.getData() ?? []
              const selected = (this.lastSearchResults ?? []).filter(isSlimSelectOption)
                .map((current) => allOptions.filter(isSlimSelectOption)
                  .find(({ value }) => value === current.value) || current)
              this.searchResultSelected = false
              resolve(selected)
            })
          }

          return new Promise((resolve, reject) => {
            if (query.length < 2) return reject("Search must be at least 2 characters")

            this.search(query).then((data: Collection[]) => {
              // Take the data and create an array of options
              // excluding any that are already selected in currentData
              const selectedIds = new Set(currentlySelectedOptions.filter(isSlimSelectOption)
                .map((optionData) => optionData.value))
              const unselected = data
                .filter(({ id }) => !selectedIds.has(id.toString()))
                .map(({ id, name }) => ({ text: name, value: id.toString() }))
              this.lastSearchResults = unselected
              resolve(unselected)
            }).catch(() => {
              reject("Error fetching search results")
            })
          })
        },
      },
    })
  }

  async search(query: string) {
    const response = await get(generateUrlWithQuery(this.urlValue, { q: query }), {
      headers: { Accept: "application/json" },
    })
    return await response.json
  }

  disconnect() {
    this.slimSelect?.destroy()
  }

  rerender() {
    this.slimSelect?.render?.moveContent()
  }
}

const isSlimSelectOption = (object: unknown): object is Option => {
  return object !== null && typeof object === "object" && "value" in object
}
